import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { UserPropertyValue } from '@store/api/projects/projectsApi';

export interface BundleProjectInfo {
  projectId: string;
  name: string;
  slug: string;
}

export interface BundleChannelInfo {
  channelId: string;
  name: string;
}

export interface BundleRollbackHealthSummary {
  reportCount: number;
  affectedInstallCount: number;
  latestReportedAt: string | null;
  latestFailedAt: string | null;
  warning: boolean;
}

export type BundleUpdateSizeStatus =
  | 'ready'
  | 'calculating'
  | 'fullBundleUsed'
  | 'failed'
  | 'notAvailable';

export interface BundleUpdateSizeInfo {
  status: BundleUpdateSizeStatus;
  size: number | null;
  baseHash?: string | null;
  baseBundleVersion?: number | null;
}

export interface TargetUserPropertyRule {
  key: string;
  values: UserPropertyValue[];
}

export interface BundleInfo {
  _id: string;
  channelId?: string;
  channelIds?: string[];
  platform: string;
  version: string;
  buildNumber?: string | null;
  runtimeVersion?: string | null;
  compatibilitySource?: 'manual' | 'fingerprint';
  fingerprint?: { schemeVersion: number; hash: string; manualBoundary: string | null } | null;
  bundleVersion?: number;
  bundleVersionLabel?: string;
  createdAt: string;
  uploadedAt?: string;
  hash?: string;
  downloadUrl?: string;
  r2Size?: number | null;
  updateSize?: BundleUpdateSizeInfo | null;
  installedCount?: number;
  isRevoked?: boolean;
  currentRolloutPercentage?: number | null;
  currentRolloutStatus?: BundleRolloutStatus | null;
  rollbackHealth?: BundleRollbackHealthSummary;
  targetedRolloutEnabled?: boolean;
  targetedUserProperties?: TargetUserPropertyRule[];
  targetedRollout?: boolean;
  targetUserProperties?: TargetUserPropertyRule[];
  isRolloutInProgress?: boolean;
  contentType?: string;
  projectId?: string;
  orgId?: string;
  expiresAt?: string | null;
  isExpired?: boolean;
  createdBy?: string;
  authorName?: string | null;
  project?: BundleProjectInfo;
  channels?: BundleChannelInfo[];
  releaseNotes?: string | null;
  __v?: number;
}

export type BundlePlatformFilter = 'all' | 'android' | 'ios';
export type BundleTimePeriodFilter = 'allTime' | 'last7Days' | 'last30Days' | 'last90Days';
export type BundleSortFilter = 'newestFirst' | 'oldestFirst';

export interface GetBundleInfosByChannelIdRequest {
  channelId: string;
  cursor?: string;
  limit?: number;
  platform?: BundlePlatformFilter;
  timePeriod?: BundleTimePeriodFilter;
  sort?: BundleSortFilter;
}

export interface GetBundleInfosByChannelIdResponse {
  items: BundleInfo[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface RevokeBundleInChannelRequest {
  channelId: string;
  bundleHash: string;
  reason?: string;
}

export interface RevokeBundleInChannelResponse {
  channelId: string;
  hash: string;
  active: boolean;
  reason?: string | null;
  createdAt: string;
}

export type BundleRolloutStatus = 'active' | 'paused' | 'completed';

export interface SetBundleRolloutRequest {
  channelId: string;
  bundleHash: string;
  rolloutPct: number;
  targetedRollout?: boolean;
  targetUserProperties?: TargetUserPropertyRule[];
  status?: BundleRolloutStatus;
}

export interface SetBundleRolloutResponse {
  channelId: string;
  hash: string;
  platform: string;
  runtimeVersion: string | null;
  bundleVersion: number;
  rolloutPct: number;
  targetedRolloutEnabled?: boolean;
  targetedUserProperties?: TargetUserPropertyRule[];
  targetedRollout?: boolean;
  targetUserProperties?: TargetUserPropertyRule[];
  status: BundleRolloutStatus;
  updatedAt?: string;
}

export interface DeleteBundleByHashRequest {
  hash: string;
  channelId?: string;
}

export interface DeleteBundleByHashResponse {
  message?: string;
  action?: 'detached' | 'deleted';
  hash: string;
  bundleVersion?: number;
  bundleVersionLabel?: string;
  projectId?: string;
  orgId?: string;
  remainingChannelIds?: string[];
}

export interface ExpireBundleByHashRequest {
  hash: string;
  channelId: string;
}

export interface ExpireBundleByHashResponse {
  message?: string;
  hash: string;
  action: BulkExpireBundleAction;
  bundleVersion?: number;
  bundleVersionLabel?: string;
  projectId?: string;
  orgId?: string;
  expiresAt?: string;
  error?: string;
}

export type BulkDeleteBundleAction = 'deleted' | 'detached' | 'retained' | 'notFound' | 'failed';
export type BulkExpireBundleAction = 'expired' | 'alreadyExpired' | 'notFound' | 'failed';
export type BulkBundleOperation = 'delete' | 'expire';

export interface BulkDeleteBundlesRequest {
  channelId: string;
  hashes: string[];
}

export interface BulkDeleteBundleResult {
  hash: string;
  action: BulkDeleteBundleAction;
  bundleVersion?: number;
  bundleVersionLabel?: string;
  projectId?: string;
  orgId?: string;
  remainingChannelIds?: string[];
  storageDeleteStatus?: {
    status: 'deleted' | 'alreadyDeleted' | 'retained' | 'failed';
    attempted: boolean;
    error?: string;
  };
  policyCleanupStatus?: {
    status: 'cleared' | 'failed';
    error?: string;
  };
  error?: string;
}

export interface BulkDeleteBundlesInlineResponse {
  message?: string;
  channelId: string;
  results: BulkDeleteBundleResult[];
  summary: Record<BulkDeleteBundleAction, number>;
}

export interface BulkExpireBundleResult {
  hash: string;
  action: BulkExpireBundleAction;
  bundleVersion?: number;
  bundleVersionLabel?: string;
  projectId?: string;
  orgId?: string;
  expiresAt?: string;
  error?: string;
}

export interface BulkExpireBundlesInlineResponse {
  message?: string;
  channelId: string;
  results: BulkExpireBundleResult[];
  summary: Record<BulkExpireBundleAction, number>;
}

export interface BulkDeleteBundlesQueuedResponse {
  mode: 'queued';
  operation: 'delete';
  channelId: string;
  jobId: string | null;
}

export interface BulkExpireBundlesQueuedResponse {
  mode: 'queued';
  operation: 'expire';
  channelId: string;
  jobId: string | null;
}

export type BulkDeleteBundlesResponse =
  | BulkDeleteBundlesInlineResponse
  | BulkDeleteBundlesQueuedResponse;
export type BulkExpireBundlesResponse =
  | BulkExpireBundlesInlineResponse
  | BulkExpireBundlesQueuedResponse;
export type BulkBundleOperationInlineResponse =
  | BulkDeleteBundlesInlineResponse
  | BulkExpireBundlesInlineResponse;

export interface BulkBundleOperationJobStatus {
  jobId: string;
  operation: BulkBundleOperation;
  state: string;
  isDone: boolean;
  isSuccess: boolean;
  data: {
    operation: BulkBundleOperation;
    orgId: string;
    projectId: string;
    channelId: string;
    hashes: string[];
    requestedByUserId: string;
    requestedAt: string;
  };
  attemptsMade: number;
  failedReason: string | null;
  result: BulkBundleOperationInlineResponse | null;
  summary: BulkBundleOperationInlineResponse['summary'] | null;
  results: BulkBundleOperationInlineResponse['results'] | null;
  progress: unknown;
  timestamp: number | null;
  processedOn: number | null;
  finishedOn: number | null;
}

export const isBulkDeleteQueuedResponse = (
  response: BulkDeleteBundlesResponse
): response is BulkDeleteBundlesQueuedResponse =>
  'mode' in response && response.mode === 'queued';

export const isBulkExpireQueuedResponse = (
  response: BulkExpireBundlesResponse
): response is BulkExpireBundlesQueuedResponse =>
  'mode' in response && response.mode === 'queued';

export interface BundleFilterOptionsResponse {
  platforms: string[];
  runtimeVersions: string[];
  versions: string[];
  authors: string[];
}

export const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(new Set(value.map(item => String(item ?? '').trim()).filter(Boolean)));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return normalizeStringArray(parsed);
      } catch {
        // fall through to comma parsing
      }
    }

    return Array.from(
      new Set(
        trimmed
          .split(',')
          .map(item => item.trim())
          .filter(Boolean)
      )
    );
  }

  return [];
};

const normalizeTargetUserPropertyRules = (value: unknown): TargetUserPropertyRule[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item): item is { key: string; values: unknown } =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as Record<string, unknown>).key === 'string'
    )
    .map(item => ({
      key: item.key,
      values: Array.isArray(item.values)
        ? item.values.filter(
            (v): v is UserPropertyValue =>
              typeof v === 'string' ||
              typeof v === 'boolean' ||
              (typeof v === 'number' && Number.isFinite(v))
          )
        : [],
    }))
    .filter(rule => rule.values.length > 0);
};

export const normalizeTargetingMetadata = <
  T extends {
    targetedRolloutEnabled?: boolean | null;
    targetedRollout?: boolean | null;
    targetedUserProperties?: unknown;
    targetUserProperties?: unknown;
  },
>(
  bundle: T
) => {
  const normalizedTargetedUserProperties = normalizeTargetUserPropertyRules(
    bundle?.targetedUserProperties ?? bundle?.targetUserProperties
  );
  const normalizedTargetedRolloutEnabled = Boolean(
    bundle?.targetedRolloutEnabled ?? bundle?.targetedRollout
  );

  return {
    ...bundle,
    targetedRolloutEnabled: normalizedTargetedRolloutEnabled,
    targetedRollout: normalizedTargetedRolloutEnabled,
    targetedUserProperties: normalizedTargetedUserProperties,
    targetUserProperties: normalizedTargetedUserProperties,
  };
};

export const bundlesApi = createApi({
  reducerPath: 'bundlesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Bundles'],
  endpoints: builder => ({
    uploadBundle: builder.mutation<
      { message?: string },
      {
        platform: string;
        version: string;
        orgSlug: string;
        projectSlug: string;
        channelName: string;
        file: File;
      }
    >({
      query: body => {
        const formData = new FormData();
        formData.append('platform', body.platform);
        formData.append('version', body.version);
        formData.append('orgSlug', body.orgSlug);
        formData.append('projectSlug', body.projectSlug);
        formData.append('channelName', body.channelName);
        formData.append('file', body.file);

        return {
          url: '/bundle/upload',
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: [{ type: 'Bundles', id: 'LIST' }],
    }),
    getBundleInfosByChannelId: builder.query<
      GetBundleInfosByChannelIdResponse,
      GetBundleInfosByChannelIdRequest
    >({
      query: ({ channelId, cursor, limit, platform, timePeriod, sort }) => ({
        url: '/bundle/getInfosByChannelId',
        params: { channelId, cursor, limit, platform, timePeriod, sort },
      }),
      transformResponse: (response: GetBundleInfosByChannelIdResponse) => ({
        ...response,
        items: Array.isArray(response?.items)
          ? response.items.map(item => normalizeTargetingMetadata(item))
          : [],
      }),
      providesTags: (_result, _error, { channelId }) => [
        { type: 'Bundles', id: channelId },
        { type: 'Bundles', id: 'LIST' },
      ],
    }),
    getBundleFilterOptions: builder.query<BundleFilterOptionsResponse, { channelId: string }>({
      query: ({ channelId }) => ({
        url: '/bundle/filters',
        params: { channelId },
      }),
      providesTags: (_result, _error, { channelId }) => [
        { type: 'Bundles', id: `filters-${channelId}` },
      ],
    }),
    revokeBundleInChannel: builder.mutation<
      RevokeBundleInChannelResponse,
      RevokeBundleInChannelRequest
    >({
      query: body => ({
        url: '/channels/revokeBundle',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Bundles', id: channelId },
        { type: 'Bundles', id: 'LIST' },
      ],
    }),
    setBundleRollout: builder.mutation<SetBundleRolloutResponse, SetBundleRolloutRequest>({
      query: body => ({
        url: '/channels/setBundleRollout',
        method: 'POST',
        data: body,
      }),
      transformResponse: (
        response: SetBundleRolloutResponse & {
          targetUserProperties?: unknown;
          targetedUserProperties?: unknown;
        }
      ) => normalizeTargetingMetadata(response),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Bundles', id: channelId },
        { type: 'Bundles', id: 'LIST' },
      ],
    }),
    deleteBundleByHash: builder.mutation<DeleteBundleByHashResponse, DeleteBundleByHashRequest>({
      query: ({ hash, channelId }) => ({
        url: `/bundle/${hash}`,
        method: 'DELETE',
        params: channelId ? { channelId } : undefined,
      }),
      invalidatesTags: [{ type: 'Bundles', id: 'LIST' }],
    }),
    expireBundleByHash: builder.mutation<ExpireBundleByHashResponse, ExpireBundleByHashRequest>({
      query: ({ hash, channelId }) => ({
        url: `/bundle/${encodeURIComponent(hash)}/expire`,
        method: 'POST',
        data: { channelId },
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Bundles', id: channelId },
        { type: 'Bundles', id: 'LIST' },
      ],
    }),
    bulkDeleteBundles: builder.mutation<BulkDeleteBundlesResponse, BulkDeleteBundlesRequest>({
      query: body => ({
        url: '/bundle/bulk/delete',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Bundles', id: channelId },
        { type: 'Bundles', id: 'LIST' },
      ],
    }),
    bulkExpireBundles: builder.mutation<BulkExpireBundlesResponse, BulkDeleteBundlesRequest>({
      query: body => ({
        url: '/bundle/bulk/expire',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Bundles', id: channelId },
        { type: 'Bundles', id: 'LIST' },
      ],
    }),
    getBulkBundleOperationJobStatus: builder.query<BulkBundleOperationJobStatus, string>({
      query: jobId => ({
        url: `/bundle/bulk/jobs/${encodeURIComponent(jobId)}`,
      }),
      providesTags: result =>
        result
          ? [
              { type: 'Bundles', id: result.data.channelId },
              { type: 'Bundles', id: 'LIST' },
            ]
          : [{ type: 'Bundles', id: 'LIST' }],
    }),
  }),
});

export const {
  useUploadBundleMutation,
  useGetBundleInfosByChannelIdQuery,
  useLazyGetBundleInfosByChannelIdQuery,
  useGetBundleFilterOptionsQuery,
  useRevokeBundleInChannelMutation,
  useSetBundleRolloutMutation,
  useDeleteBundleByHashMutation,
  useExpireBundleByHashMutation,
  useBulkDeleteBundlesMutation,
  useBulkExpireBundlesMutation,
  useLazyGetBulkBundleOperationJobStatusQuery,
} = bundlesApi;
